import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { blueGrey, deepPurple, red, grey } from '@mui/material/colors';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CategoryIcon from '@mui/icons-material/Category';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpIcon from '@mui/icons-material/Help';
import { Skeleton } from '@mui/material';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import UserContext from '../../../../shared-components/contexts/UserContext';
import AlertIcon from '../../../../shared-components/Icons/AlertIcon';
import DataInsightIcon from '../../../../shared-components/Icons/DataInsightIcon';
import EncouragementIcon from '../../../../shared-components/Icons/EncouragementIcon';
import UpdateIcon from '../../../../shared-components/Icons/UpdateIcon';
import { INotation } from '../../../../shared-interfaces/Notation/INotation';
import { GetCoreUserFormattedName } from '../../../../shared-interfaces/User/ICoreUser';
import { NotationType } from '../../../../shared-types/Notation/NotationType';
import NotationDetails from './NotationDetails';

interface INotationListItem {
  notationSearchResponse: INotation;
  setAddResponseTo: (x: INotation) => void;
  hasBeenSeen: boolean;
  setHasBeenSeen: () => void;
  onUpdate: (n: number) => void;
}

const useStyles = makeStyles(theme => ({
  avatar: {
    width: '60px',
    height: '60px',
  },
  badge: {
    top: '8px',
    right: '2px',
  },
  row: {
    borderBottom: '1px solid #dcdcdc',
    marginTop: '4px',
  },
  titleText: {
    fontWeight: 600,
    [theme.breakpoints.down('lg')]: {
      fontSize: '0.95rem',
    },
  },
  name: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: '#0066cc',
  },
  caption: {
    fontSize: '0.85rem',
    fontStyle: 'italic',
  },
  recentActivityChip: {
    marginRight: '4px',
    color: deepPurple[500],
    borderColor: deepPurple[500],
    '& svg': {
      color: deepPurple[500],
    },
  },
  assignmentChip: {
    marginRight: '4px',
    backgroundColor: blueGrey[600],
    border: 'none',
    borderRadius: '16px',
    color: 'white',
  },
  categoryChip: {
    marginRight: '4px',
    marginBottom: '4px',
    backgroundColor: '#fff',
    border: 'none',
    color: grey[900],
  },
  listItemText: {
    [theme.breakpoints.only('xs')]: {
      maxWidth: '70%',
    },
    [theme.breakpoints.up('sm')]: {
      maxWidth: '100%',
    },
    paddingRight: '8px',
  },
  time: {
    fontWeight: 600,
  },
  chipContainer: {
    marginTop: '4px',
    marginBottom: '4px',
  },
  heading: {
    fontWeight: 600,
  },
  listItemRight: {
    marginTop: '8px',
    minWidth: '160px',
  },
  detailContainer: {
    '& p': {
      margin: 0,
    },
    marginBottom: '8px',
  },
}));

export const NotationListItemSkeleton = () => {
  const classes = useStyles();
  return (
    <ListItem aria-busy="true" className={classes.row} alignItems="flex-start">
      <ListItemAvatar>
        <Skeleton variant="circular" className={classes.avatar} />
      </ListItemAvatar>
      <ListItemText
        style={{ paddingLeft: '16px' }}
        className={classes.listItemText}
        primaryTypographyProps={{ variant: 'h3' }}
        disableTypography
        primary={<Skeleton variant="text" width={200} />}
        secondary={
          <>
            <Skeleton variant="text" width={300} />
            <Skeleton variant="text" width={250} />
          </>
        }
      />
      <div className={classes.listItemRight}>
        <div style={{ fontWeight: 600 }}>
          <Skeleton variant="text" />
        </div>
      </div>
    </ListItem>
  );
};

const NotationListItem = (props: INotationListItem) => {
  const user = useContext(UserContext);
  const classes = useStyles();
  const theme = useTheme();

  const isXS = useMediaQuery(theme.breakpoints.only('xs'));
  const name = GetCoreUserFormattedName(
    props.notationSearchResponse.student,
    false,
  );

  const [expandDetails, setExpandDetails] = useState(false);
  const [isOpen, setIsOpen] = useState(
    props.notationSearchResponse.notationType === NotationType.Alert &&
      props.notationSearchResponse.alertStatus === 'Open',
  );
  const [seen, setSeen] = useState(props.hasBeenSeen);
  const [assignedTo, setAssignedTo] = useState(
    props.notationSearchResponse.assignedTo,
  );

  useEffect(() => {
    // Sanity check for IsOpen
    if (props.notationSearchResponse.notationType !== NotationType.Alert) {
      setIsOpen(false);
    } else if (props.notationSearchResponse.alertStatus !== 'Open') {
      setIsOpen(false);
    } else if (
      props.notationSearchResponse.notationType === NotationType.Alert &&
      props.notationSearchResponse.alertStatus === 'Open'
    ) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [props.notationSearchResponse]);

  const hasCategories = props.notationSearchResponse.categories.length > 0;

  const ChipContainer = () => {
    return (
      <>
        {props.notationSearchResponse.categories.map((category, i) => {
          return (
            <Chip
              key={i}
              variant="outlined"
              label={category.name}
              avatar={<CategoryIcon style={{ color: grey[900] }} />}
              color="default"
              size="small"
              className={classes.categoryChip}
            />
          );
        })}
      </>
    );
  };

  return (
    <li
      key={props.notationSearchResponse.indexId}
      className={classes.row}
      style={
        isOpen
          ? {
              borderLeft: `${isXS ? '5px' : '8px'} solid ${red[600]}`,
            }
          : {
              borderLeft: `${isXS ? '5px' : '8px'} solid transparent`,
            }
      }
    >
      <Collapse in={expandDetails} collapsedSize={100}>
        <ListItem component="div" alignItems="flex-start">
          <ListItemAvatar style={{ marginRight: '20px' }}>
            <Badge
              badgeContent={
                props.notationSearchResponse.notationType ===
                NotationType.Alert ? (
                  <AlertIcon />
                ) : props.notationSearchResponse.notationType ===
                  NotationType.Encouragement ? (
                  <EncouragementIcon />
                ) : props.notationSearchResponse.notationType ===
                  NotationType.Update ? (
                  <UpdateIcon />
                ) : props.notationSearchResponse.notationType ===
                  NotationType.DataInsight ? (
                  <DataInsightIcon />
                ) : (
                  <HelpIcon />
                )
              }
              anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
              className={classes.badge}
            >
              <Avatar
                alt={name}
                className={classes.avatar}
                src={
                  props.notationSearchResponse.student.profileImageFilePath ??
                  props.notationSearchResponse.student
                    .institutionProfileImageFilePath
                }
              />
            </Badge>
          </ListItemAvatar>
          <ListItemText
            className={classes.listItemText}
            primaryTypographyProps={{ variant: 'h3' }}
            disableTypography
            primary={
              <Box display="flex">
                <Box flexGrow={1}>
                  <div style={{ width: '100%' }}>
                    <Typography variant="h3" className={classes.titleText}>
                      {props.notationSearchResponse.notationType ===
                      NotationType.Alert
                        ? `${isOpen ? 'Open' : 'Lowered'} Alert for `
                        : props.notationSearchResponse.notationType ===
                          NotationType.Encouragement
                        ? 'Encouragement for '
                        : props.notationSearchResponse.notationType ===
                          NotationType.Update
                        ? 'Update for '
                        : props.notationSearchResponse.notationType ===
                          NotationType.DataInsight
                        ? 'Data Insight for '
                        : ''}
                      <Link
                        target="_blank"
                        href={`/beacon/students/advise/student/${props.notationSearchResponse.student.externalId}`}
                        aria-label={`Go to ${name} profile`}
                        data-clarity-mask="true"
                      >
                        <span className={classes.name} data-clarity-mask="true">
                          {name}
                        </span>
                      </Link>
                    </Typography>
                  </div>
                </Box>
                <Box display="flex" justifyContent="flex-end">
                  {dayjs(user.previouslyLoggedIn).isBefore(
                    dayjs(props.notationSearchResponse.modifiedOn),
                  ) &&
                    !seen && (
                      <Box display="flex" justifyContent="flex-end">
                        {!isXS ? (
                          <Chip
                            variant="outlined"
                            label={'New Activity'}
                            size="small"
                            className={classes.recentActivityChip}
                            icon={<AccessTimeIcon />}
                          />
                        ) : (
                          <AccessTimeIcon
                            className={classes.recentActivityChip}
                          />
                        )}
                      </Box>
                    )}
                </Box>
              </Box>
            }
            secondary={
              <>
                <Typography variant="caption" className={classes.caption}>
                  by{' '}
                  <span data-clarity-mask="true">
                    {GetCoreUserFormattedName(
                      props.notationSearchResponse.createdBy,
                      false,
                    )}
                  </span>{' '}
                  -{' '}
                  {typeof props.notationSearchResponse.createdOn ===
                  'string' ? (
                    <>
                      {`${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString(
                        [],
                        {
                          hour: '2-digit',
                          minute: '2-digit',
                        },
                      )}`}
                    </>
                  ) : (
                    <>
                      {`${props.notationSearchResponse.createdOn.toLocaleDateString()} at ${props.notationSearchResponse.createdOn.toLocaleTimeString(
                        [],
                        {
                          hour: '2-digit',
                          minute: '2-digit',
                        },
                      )}`}
                    </>
                  )}
                </Typography>
                {assignedTo && (
                  <Typography variant="caption" className={classes.caption}>
                    {' '}
                    | Assigned To:{' '}
                    <span style={{ fontWeight: 600 }} data-clarity-mask="true">
                      {GetCoreUserFormattedName(assignedTo, false)}
                    </span>
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  component="div"
                  className={classes.detailContainer}
                  data-clarity-mask="true"
                  dangerouslySetInnerHTML={{
                    __html: props.notationSearchResponse.details,
                  }}
                />
              </>
            }
          />
        </ListItem>
        {hasCategories && (
          <div style={{ margin: '9px 0', padding: '0 94px' }}>
            <Typography component="h4" className={classes.heading}>
              Categories:
            </Typography>
            <span data-clarity-mask="true">
              <ChipContainer />
            </span>
          </div>
        )}
        <NotationDetails
          open={expandDetails}
          notation={props.notationSearchResponse}
          setAddResponseTo={props.setAddResponseTo}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          assignedTo={assignedTo}
          setAssignedTo={u => setAssignedTo(u)}
          onUpdate={props.onUpdate}
        />
      </Collapse>
      <Button
        variant="text"
        size="small"
        color="primary"
        onClick={() => {
          if (!seen) {
            props.setHasBeenSeen();
            setSeen(true);
          }
          setExpandDetails(!expandDetails);
        }}
        fullWidth
      >
        {expandDetails ? (
          <>
            <ExpandLessIcon /> View Less
          </>
        ) : (
          <>
            <ExpandMoreIcon /> View More
          </>
        )}
      </Button>
    </li>
  );
};

export default NotationListItem;
